/**
 * Gemensam formatering av datum och tid i svenska. Används i stället för lokala
 * kopior i komponenterna. Tomt värde ger ett tankstreck.
 *
 * Skicka med användarens tidszon (från useAuth().timezone) när tidpunkten ska visas
 * i användarens lokala tid; utan tidszon används webbläsarens.
 */

const DASH = '–';

/** Datum utan tid, t.ex. "3 sep. 2026". Tar YYYY-MM-DD eller en ISO-tidpunkt. */
export function fmtDate(value: string | null | undefined, opts?: Intl.DateTimeFormatOptions): string {
    if (!value) return DASH;
    try {
        const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(value + 'T00:00:00') : new Date(value);
        if (isNaN(d.getTime())) return String(value);
        return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric', ...opts });
    } catch { return String(value); }
}

/** Datum och klockslag, t.ex. "3 sep. 14:05". */
export function fmtDateTime(value: string | number | Date | null | undefined, timezone?: string | null): string {
    if (value == null || value === '') return DASH;
    try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return String(value);
        return d.toLocaleString('sv-SE', {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            ...(timezone ? { timeZone: timezone } : {}),
        });
    } catch { return String(value); }
}

/** Fullt datum och tid med sekunder, t.ex. "2026-09-03 14:05:12". */
export function fmtDateTimeFull(value: string | number | Date | null | undefined, timezone?: string | null): string {
    if (value == null || value === '') return DASH;
    try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return String(value);
        return d.toLocaleString('sv-SE', timezone ? { timeZone: timezone } : undefined);
    } catch { return String(value); }
}

/** Bara klockslag, t.ex. "14:05". */
export function fmtTime(value: string | number | Date | null | undefined, timezone?: string | null): string {
    if (value == null || value === '') return DASH;
    try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return String(value);
        return d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit', ...(timezone ? { timeZone: timezone } : {}) });
    } catch { return String(value); }
}

/** Dagens datum som YYYY-MM-DD i webbläsarens tidszon. */
export const todayStr = (): string => new Date().toLocaleDateString('sv-SE');
